/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, Grid, LinearProgress, MenuItem, Paper, Snackbar, SnackbarCloseReason, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField,  VForm, useVForm, IVFormErrors, VSwitch, VTextFieldNumber } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/contaBancaria/ContaBancariaService';
import { ProdutorService } from '../../../shared/services/api/cadastros/ProdutorService';

type TTipoContaFinanceira = 'banco' | 'caixa' | 'cofre' | 'recebiveis';

interface IFormData {
  nomebanco?: string;
  agencia?: string;
  numeroconta?: string;
  titular: string;
  status: boolean;
  banco?: string;
  tipo: TTipoContaFinanceira;
}

const TIPOS_CONTA_FINANCEIRA: { valor: TTipoContaFinanceira; label: string }[] = [
  { valor: 'banco', label: 'Banco' },
  { valor: 'caixa', label: 'Caixa' },
  { valor: 'cofre', label: 'Cofre' },
  { valor: 'recebiveis', label: 'Recebíveis' },
];

// Banco/Nº do banco/agência/nº da conta só fazem sentido pra tipo 'banco' —
// caixa, cofre e recebíveis não têm essa informação bancária.
const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  nomebanco: yup.string().when('tipo', {
    is: 'banco',
    then: (schema) => schema.required().max(30),
    otherwise: (schema) => schema.optional(),
  }),
  agencia: yup.string().when('tipo', {
    is: 'banco',
    then: (schema) => schema.required().max(34),
    otherwise: (schema) => schema.optional(),
  }),
  numeroconta: yup.string().when('tipo', {
    is: 'banco',
    then: (schema) => schema.required().max(16),
    otherwise: (schema) => schema.optional(),
  }),
  titular: yup.string().required(),
  status: yup.boolean().optional().default(true),
  banco: yup.string().when('tipo', {
    is: 'banco',
    then: (schema) => schema.required().max(34),
    otherwise: (schema) => schema.optional(),
  }),
  tipo: yup.mixed<TTipoContaFinanceira>().oneOf(['banco', 'caixa', 'cofre', 'recebiveis']).required('Selecione o tipo da conta'),
});

export const DetalheDeContaBancaria: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>(); 
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState('');
  // Controla se os campos de dado bancário aparecem no formulário — só se
  // aplicam ao tipo 'banco' (ver formValidationSchema).
  const [tipoSelecionado, setTipoSelecionado] = useState<TTipoContaFinanceira>('banco');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  


  const [value, setValue] = useState([]);
        
  const [openConfirm, setOpenConfirm] = useState(false);
          
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      navigate('/contabancaria');
    }
  };
        
  const handleDelete = () => {
    setOpenConfirm(true); 
  };
        
  const handleConfirmDelete = () => {
    ContaBancariaService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
        
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContaBancariaService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao(result.titular);
            setTipoSelecionado(result.tipo || 'banco');
            formRef.current?.setData(result);

          }
        });
    } else {
      setTipoSelecionado('banco');
      formRef.current?.setData({
        nomebanco: '',
        agencia: '',
        numeroconta: '',

        titular: '',
        banco: '',
        tipo: 'banco',
      });
    }
    
  }, [id]);
  
  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ContaBancariaService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          ContaBancariaService
            .updateById(id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando conta financeira..."
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Nova Conta Financeira' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvar
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando = {isLoading}
          mostrarBotaoSalvarEFecharCarregando = {isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando = {isLoading}
          mostrarBotaoImpressao = {false}
          disableBotaoSalvar={isLoading || isSaving}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/contabancaria')}
          mostrarBotaoVoltarCarregando = {isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contabancaria/detalhe/novo')}
          mostrarBotaoNovoCarregando = {isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"  
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
              Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
              Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid container item direction="row" >
              <Grid item xs={12}  sm={12} md={6} lg={8} xl={8}> 
                <Grid container item direction="row" spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={12} xl={12}>          
                    <VTextField
                      fullWidth
                      size='small'
                      name='titular'
                      disabled={isLoading}
                      label='Descrição da Conta'
                      onChange={e => setDescricao(e.target.value)}
                    />
                  </Grid>
                 
                  <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                    <VTextField
                      select
                      fullWidth
                      size='small'
                      name='tipo'
                      disabled={isLoading}
                      label='Tipo'
                      valor={(novoTipo) => setTipoSelecionado((novoTipo as TTipoContaFinanceira) || 'banco')}
                    >
                      {TIPOS_CONTA_FINANCEIRA.map(({ valor, label }) => (
                        <MenuItem key={valor} value={valor}>{label}</MenuItem>
                      ))}
                    </VTextField>
                  </Grid>

                  {/* Banco/Nº do banco/Agência/Nº da conta só se aplicam a
                      uma conta do tipo 'Banco' (ver formValidationSchema). */}
                  {tipoSelecionado === 'banco' && (
                    <>
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <VTextField
                          fullWidth
                          size='small'
                          name='nomebanco'
                          disabled={isLoading}
                          label='Banco'
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <VTextFieldNumber
                          fullWidth
                          size='small'
                          name='banco'
                          disabled={isLoading}
                          label='Nº do Banco'
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <VTextField
                          fullWidth
                          size='small'
                          name='agencia'
                          disabled={isLoading}
                          label='Agência'
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>
                        <VTextField
                          fullWidth
                          size='small'
                          name='numeroconta'
                          disabled={isLoading}
                          label='Nº da Conta'
                        />
                      </Grid>
                    </>
                  )}

                  {(id != 'novo') && (
                    <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                      <VSwitch
                        size='small'
                        name='status'
                        label='Status'
                        fdisabled={isLoading}
                        aliamento='start'
                      />
                    </Grid>
                  )}

                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>

    </LayoutBaseDePagina>
    </>
  );
};